import { PO_SENDER } from './productionPo';
import type { ProductionPo } from './productionPo';

/**
 * The printed Production PO.
 *
 * This component receives a ProductionPo and nothing else. It never sees an
 * order row, so there is no price, cost, margin or total within reach — which
 * is the point, and what productionPo.test.ts asserts by reading this file.
 *
 * Every rule below exists because the CG-PO-#1040 sample broke it:
 *   - items are an array of <li>, not a joined string that ran two products
 *     together on one line
 *   - a SKU renders only when there is one, so no empty "()" is printed
 *   - the ship-to block is printed line for line as typed, never re-joined
 *   - a design file is an <a> whose text is its own label or its URL, never
 *     the literal word "link"
 *   - shipping is not an item, because a factory cannot manufacture a fee
 *
 * PAYMENT STATUS is deliberately absent. It reported whether the CLIENT had
 * paid their invoice and printed "PAID" to the party being paid, which reads
 * as confirmation that THEY have been. It is also the only commercial fact on
 * a sheet whose entire design is that it carries no money.
 */

function EmptyLine({ children }: { children: string }) {
  return <span className="po-empty">{children}</span>;
}

export function ProductionPoDocument({
  po,
  superseded = false,
  notice,
  children,
}: {
  po: ProductionPo;
  /** Prints SUPERSEDED across the sheet. Only ever true for a frozen revision. */
  superseded?: boolean;
  /** One line above the items explaining why this revision was replaced. */
  notice?: string;
  /** Extra sections — client approval, approved artwork — rendered before the footer. */
  children?: React.ReactNode;
}) {
  return (
    <article className={superseded ? 'po-doc po-doc--superseded' : 'po-doc'}>
      {/* Printed too, not just shown on screen — a PDF saved from this and
          emailed on has to carry the same warning the screen did. */}
      {superseded ? <div className="po-doc-watermark">SUPERSEDED</div> : null}

      <header className="po-head">
        {/* The real mark, from public/ so it prints and survives a saved PDF.
            The wordmark stays as the alt: an image that fails to load renders
            its text in the same slot rather than a broken icon on a contract
            document. */}
        <img className="po-brand-logo" src="/4ocean-logo-blue.png" alt="4ocean" />
        <div className="po-head-right">
          <h1 className="po-title">PRODUCTION PO</h1>
          <div className="po-head-meta">{po.poNumber}</div>
          <div className="po-head-meta">{po.issuedOn}</div>
        </div>
      </header>

      <div className="po-parties">
        <section className="po-party">
          <h2 className="po-party-label">FROM</h2>
          <p className="po-party-name">{PO_SENDER.name}</p>
          {PO_SENDER.addressLines.map((line) => (
            <p key={line} className="po-party-line">
              {line}
            </p>
          ))}
          <p className="po-party-line">{PO_SENDER.email}</p>
        </section>

        <section className="po-party">
          <h2 className="po-party-label">MANUFACTURER</h2>
          <p className="po-party-name">{po.manufacturer}</p>
          <p className="po-party-line">Attn: {po.manufacturerAttn}</p>
          {po.manufacturerAddressLines.map((line, index) => (
            <p key={`${String(index)}-${line}`} className="po-party-line">
              {line}
            </p>
          ))}
        </section>

        <section className="po-party">
          <h2 className="po-party-label">SHIP TO</h2>
          {po.shipToLines.length === 0 ? (
            <p className="po-party-line">
              <EmptyLine>No delivery address on this order</EmptyLine>
            </p>
          ) : (
            po.shipToLines.map((line, index) => (
              <p
                key={`${String(index)}-${line}`}
                className={index === 0 ? 'po-party-name' : 'po-party-line'}
              >
                {line}
              </p>
            ))
          )}
        </section>
      </div>

      <table className="po-details">
        <tbody>
          <tr>
            <th scope="row">PO NUMBER</th>
            <td>{po.poNumber}</td>
          </tr>
          {/* The client's own reference for this job. It is the Shopify order
              number — cg_orders.order_no IS that number, there is no second
              field holding it — so the old sheet's separate SHOPIFY ORDER #
              row printed the same value twice under two names and left a
              factory to guess which one to quote. One row, named for what the
              number means to the people reading it. */}
          <tr>
            <th scope="row">CLIENT PO #</th>
            <td>{po.orderNo}</td>
          </tr>
          <tr>
            <th scope="row">TARGET SHIP DATE</th>
            <td>
              {po.targetShipDate === null ? (
                <EmptyLine>Not yet committed</EmptyLine>
              ) : (
                po.targetShipDate
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {notice === undefined || notice === '' ? null : <p className="po-doc-notice">{notice}</p>}

      <section className="po-section">
        <h2 className="po-section-label">ITEMS</h2>
        {po.lines.length === 0 ? (
          <p className="po-party-line">
            <EmptyLine>No items on this order</EmptyLine>
          </p>
        ) : (
          <ul className="po-items">
            {po.lines.map((line, index) => (
              <li key={`${String(index)}-${line.name}`} className="po-item">
                <span className="po-item-qty">{line.units} ×</span>{' '}
                <span className="po-item-name">{line.name}</span>
                {line.sku === '' ? null : <span className="po-item-sku"> ({line.sku})</span>}
                {line.spec === '' ? null : <span className="po-item-spec"> — {line.spec}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {po.notes === '' && po.designLinks.length === 0 ? null : (
        <section className="po-section">
          <h2 className="po-section-label">NOTES TO MANUFACTURER</h2>
          {po.notes === '' ? null : <p className="po-notes">{po.notes}</p>}
          {po.designLinks.length === 0 ? null : (
            <ul className="po-links">
              {po.designLinks.map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.name.trim() === '' ? link.url : link.name}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {children}

      <footer className="po-foot">
        4ocean Public Benefit Corporation &nbsp;|&nbsp; Corporate Gifting &nbsp;|&nbsp; Confidential
      </footer>
    </article>
  );
}
